import { Blueprint, computable, HtmlScope, Infer, iterator, observable, ownEffect, passthruLayout, watch } from "@chorda/core"
import { ReactDomEvents } from "@chorda/react"
import * as director from 'director'

import 'todomvc-common/base.css'
import 'todomvc-app-css/index.css'

type Todo = {
    id: number
    text: string
    completed?: boolean
}

type TodoMvcScope = {
    newTodo: string
    todos: Todo[]
    hasTodos: boolean
    filteredTodos: Todo[]
    isAllCompleted: boolean
    hasCompleted: boolean
    activeCount: number
}

type TodoMvcActions = {
    addTodo: () => void
    markTodoCompleted: (id: number, value: boolean) => void
    deleteTodo: (id: number) => void
    toggleAll: (value: boolean) => void
    clearCompleted: () => void
    changeTodoText: (id: number, text: string) => void
}

type Router = {
    route: string
}

type RouterScope = {
    router: Router
}
type LocalStore = {
    save: (data: any) => void
    load: () => any
}

type LocalStoreScope = {
    store: LocalStore
}


const useRouter = () : Router => {

    const router = observable({route: null})

    const r = new director.Router({
        '/': () => router.route.$value = 'all',
        '/active': () => router.route.$value = 'active',
        '/completed': () => router.route.$value = 'completed',
    });

    (r as any).init('/')

    return router
}

const useLocalStore = () : LocalStore => {

    const LS_KEY = 'chorda-todomvc'

    return {
        save: function (data) {
            localStorage.setItem(LS_KEY, JSON.stringify(data))
        },
        load: function () {
            return JSON.parse(localStorage.getItem(LS_KEY)) || []
        }
    }
}

const pluralize = (count: number, word: string) => {
    return count === 1 ? word : word + 's';
}

const filters = {
    all: () => true,
    completed: (todo) => !!todo.completed,
    active: (todo) => !todo.completed
} as {[key: string]: (todo: Todo) => boolean}



export default () : Infer.Blueprint<TodoMvcScope&RouterScope&LocalStoreScope&TodoMvcActions&HtmlScope, ReactDomEvents> => {
    return {
        templates: {
            app: {
                tag: 'section',
                css: 'todoapp',
                templates: {
                    header: {
                        tag: 'header',
                        css: 'header',
                        templates: {
                            title: {tag: 'h1', text: 'todos'},
                            input: {
                                tag: 'input',
                                css: 'new-todo',
                                dom: {
                                    placeholder: 'What needs to be done?',
                                    autoFocus: true,
                                },
                                events: {
                                    $dom: {
                                        input: (e, {newTodo}) => {
                                            const el = e.currentTarget as HTMLInputElement
                                            newTodo.$value = el.value
                                        },
                                        keyDown: (e, {addTodo}) => {
                                            if (e.key == 'Enter') {
                                                addTodo()
                                            }
                                        }
                                    }
                                },
                                joints: {
                                    fixReactValueChange: ({newTodo, $dom}) => {

                                        watch(() => {
                                            if ($dom.$value) {
                                                const el = ($dom.$value as HTMLInputElement)
                                                if (el.value != newTodo.$value) {
                                                    el.value = newTodo.$value
                                                }
                                            }
                                        }, [newTodo, $dom])

                                    }
                                }
                            }
                        }
                    },
                    main: {
                        tag: 'section',
                        css: 'main',
                        templates: {
                            toggleAll: {
                                layout: passthruLayout,
                                items: [
                                    {
                                        tag: 'input', 
                                        css: 'toggle-all',
                                        dom: {type: 'checkbox', id: 'toggle-all'},
                                        reactions: {
                                            isAllCompleted: v => ({dom: {checked: v}})
                                        },
                                        events: {
                                            $dom: {
                                                change: (e, {toggleAll}) => {
                                                    const el = e.currentTarget as HTMLInputElement
                                                    toggleAll(el.checked)
                                                }
                                            }
                                        }
                                    },
                                    {tag: 'label', text: 'Mark all as complete', dom: {htmlFor: 'toggle-all'}}
                                ]
                            },
                            list: {
                                tag: 'ul',
                                css: 'todo-list',
                                defaultItem: <Blueprint<{todo: Todo, editing: boolean, value: string}&TodoMvcScope&TodoMvcActions&HtmlScope, ReactDomEvents>>{
                                    tag: 'li',
                                    templates: {
                                        view: {
                                            css: 'view',
                                            items: [
                                                {
                                                    tag: 'input', 
                                                    css: 'toggle', 
                                                    dom: {type: 'checkbox'},
                                                    events: {
                                                        $dom: {
                                                            change: (e, {markTodoCompleted, todo}) => {
                                                                const el = e.currentTarget as HTMLInputElement
                                                                markTodoCompleted(todo.id, el.checked)
                                                            }
                                                        }
                                                    },
                                                    reactions: {
                                                        todo: v => ({dom: {checked: (v && v.completed) || false}})
                                                    }
                                                },
                                                {
                                                    tag: 'label',
                                                    reactions: {
                                                        todo: v => ({text: v && v.text})
                                                    }
                                                },
                                                {
                                                    tag: 'button', 
                                                    css: 'destroy',
                                                    events: {
                                                        $dom: {
                                                            click: (e, {deleteTodo, todo}) => {
                                                                deleteTodo(todo.id)
                                                            }
                                                        }
                                                    }
                                                }
                                            ],
                                            events: {
                                                $dom: {
                                                    doubleClick: (e, {editing}) => {
                                                        editing.$value = true
                                                    }
                                                }
                                            }
                                        },
                                        edit: {
                                            tag: 'input',
                                            css: 'edit',
                                            events: {
                                                $dom: {
                                                    change: (e, {value}) => {
                                                        const el = e.currentTarget as HTMLInputElement
                                                        value.$value = el.value
                                                    },
                                                    keyDown: (e, {editing, changeTodoText, value, todo}) => {
                                                        if (e.key == 'Escape') {
                                                            editing.$value = false
                                                        }
                                                        else if (e.key == 'Enter') {
                                                            editing.$value = false
                                                            changeTodoText(todo.id, value)
                                                        }
                                                    },
                                                    blur: (e, {editing}) => {
                                                        editing.$value = false
                                                    }
                                                }
                                            },
                                            joints: {
                                                autoFocus: ({$dom, editing, $renderer, $patcher, todo, value}) => {

                                                    watch(() => {
                                                        if (editing.$value) {
                                                            $patcher.queue($renderer.effect(() => {
                                                                $dom.$value.focus()
                                                            }))
                                                            value.$value = todo.text
                                                        }
                                                    }, [editing, $dom])

                                                },
                                                fixReactValueChange: ({value, $dom}) => {

                                                    watch(() => {
                                                        if ($dom.$value) {
                                                            const el = ($dom.$value as HTMLInputElement)
                                                            if (el.value != value.$value) {
                                                                el.value = value.$value
                                                            }
                                                        }
                                                    }, [value, $dom])
            
                                                }
                                            },
                                        }
                                    },
                                    reactions: {
                                        todo: v => ({classes: {completed: (v && v.completed) || false}}), // FIXME
                                        editing: v => ({classes: {editing: v}})
                                    },
                                    defaults: {
                                        editing: () => observable(false),
                                        value: () => observable(''),
                                    },
                                },
                                reactions: {
                                    filteredTodos: v => ({items: iterator(v, 'todo')})
                                }
                            }
                        }
                    },
                    footer: {
                        tag: 'footer',
                        css: 'footer',
                        templates: {
                            count: {
                                tag: 'span',
                                css: 'todo-count',
                                items: [
                                    {
                                        tag: 'strong', 
                                        reactions: {
                                            activeCount: v => ({text: String(v)})
                                        }
                                    },
                                    {
                                        tag: false, 
                                        text: ' item left',
                                        reactions: {
                                            activeCount: v => ({text: ` ${pluralize(v, 'item')} left`})
                                        }
                                    }
                                ]
                            },
                            filters: <Blueprint<{selected: boolean, name: string}&RouterScope>>{
                                tag: 'ul',
                                css: 'filters',
                                defaultItem: {
                                    tag: 'li',
                                    templates: {
                                        content: {
                                            tag: 'a',
                                            reactions: {
                                                selected: v => ({classes: {selected: v}})
                                            }
                                        }
                                    },
                                    defaults: {
                                        selected: $ => computable(() => {
                                            return $.router.route == $.name
                                        })
                                    }
                                },
                                items: [
                                    {
                                        templates: {
                                            content: {
                                                dom: {href: '#/'},
                                                text: 'All'
                                            }
                                        },
                                        defaults: {
                                            name: () => 'all'
                                        }
                                    },
                                    {
                                        templates: {
                                            content: {
                                                dom: {href: '#/active'},
                                                text: 'Active'
                                            }
                                        },
                                        defaults: {
                                            name: () => 'active'
                                        }
                                    },
                                    {
                                        templates: {
                                            content: {
                                                dom: {href: '#/completed'},
                                                text: 'Completed'
                                            }
                                        },
                                        defaults: {
                                            name: () => 'completed'
                                        }
                                    },
                                ]
                            },
                            clear: {
                                tag: 'button',
                                css: 'clear-completed',
                                text: 'Clear completed',
                                events: {
                                    $dom: {
                                        click: (e, {clearCompleted}) => {
                                            clearCompleted()
                                        }
                                    }
                                }
                            }
                        },
                        reactions: {
                            hasCompleted: v => ({components: {clear: v}})
                        }
                    }
                },
                reactions: {
                    hasTodos: v => ({components: {
                        header: true,
                        main: v,
                        footer: v,
                    }})
                }
            },
            footer: {
                tag: 'footer',
                css: 'info',
                items: [
                    {tag: 'p', text: 'Double-click to edit a todo'},
                    {tag: 'p', items: [
                        {tag: false, text: 'Created by '},
                        {tag: 'a', text: 'Eliace', dom: {href: 'http://todomvc.com'}},
                    ]},
                    {tag: 'p', items: [
                        {tag: false, text: 'Part of '},
                        {tag: 'a', text: 'TodoMVC', dom: {href: 'http://todomvc.com'}},
                    ]},
                ]
            }
        },
        // init: () => {

        //     const router = useRouter()
        //     const store = useLocalStore()

        //     const newTodo = observable('')
        //     const todos = observable(store.load())

        //     return {
        //         router,
        //         store,
        //         newTodo,
        //         todos,
        //     }
        // },
        defaults: {
            router: () => useRouter(),
            store: () => useLocalStore(),
            newTodo: () => observable(''),
            todos: $ => observable($.store.load()),
            hasTodos: $ => computable(() => $.todos.length > 0),
            addTodo: $ => () => {
                $.todos.$value = [...$.todos.$value, {text: $.newTodo.$value, id: new Date().getTime()}]
                $.newTodo.$value = ''
            },
            markTodoCompleted: $ => (id, value) => {
                const idx = $.todos.findIndex(todo => todo.id == +id)
                $.todos[idx].completed = value
            },
            filteredTodos: $ => computable(() => {
                return $.todos.filter(filters[$.router.route || 'all'])
            }),
            deleteTodo: $ => (id) => {
                $.todos.$value = $.todos.filter(todo => todo.id != +id)
            },
            toggleAll: $ => (value) => {
                $.todos.$value = $.todos.map(todo => ({...todo, completed: value}))
            },
            isAllCompleted: $ => computable(() => {
                return $.filteredTodos.length > 0 && $.filteredTodos.filter(filters['completed']).length == $.filteredTodos.length
            }),
            hasCompleted: $ => computable(() => {
                return $.todos.filter(filters['completed']).length > 0
            }),
            activeCount: $ => computable(() => {
                return $.todos.filter(filters['active']).length
            }),
            clearCompleted: $ => () => {
                $.todos.$value = $.todos.filter(filters['active'])
            },
            changeTodoText: $ => (id, text) => {
                const idx = $.todos.findIndex(todo => todo.id == +id)
                $.todos[idx].text = text
            }
        },
        joints: {
            storeTodos: ({store, todos}) => {

                watch(() => {
                    if (todos.$value) {
                        store.save(todos)
                    }
                }, [todos])

            }
        }
    }
}